// filepath: resources/assets/front/js/components/accordion.js

/**
 * Accordion Component
 * Gestion des accordéons avec animations et accessibilité
 * Compatible avec AccordionGenerator.php
 */

// Store pour les instances d'accordéons
const accordionInstances = new Map();

/**
 * Classe Accordion
 */
class Accordion {
    constructor(element, options = {}) {
        this.element = element;
        this.id = element.id || `accordion-${Date.now()}`;
        
        // Lire les options depuis les data-attributes (générés par AccordionGenerator.php)
        this.options = {
            // Comportement
            allowMultiple: this.parseBoolean(element.dataset.allowMultiple, options.allowMultiple, false),
            duration: parseInt(element.dataset.duration) || options.duration || 300,
            easing: element.dataset.easing || options.easing || 'ease-out',
            openFirst: this.parseBoolean(element.dataset.openFirst, options.openFirst, true),
            
            // Style
            style: element.dataset.style || options.style || 'default',
            iconType: element.dataset.iconType || options.iconType || 'chevron',
            iconPosition: element.dataset.iconPosition || options.iconPosition || 'right',
            iconRotate: this.parseBoolean(element.dataset.iconRotate, options.iconRotate, true),
            
            // Override avec options passées
            ...options
        };
        
        // Éléments
        this.items = [];
        this.headers = element.querySelectorAll('.accordion-header');
        this.contents = element.querySelectorAll('.accordion-content');
        
        // État
        this.isInitialized = false;
        
        this.init();
    }
    
    /**
     * Parser un booléen depuis string ou valeur
     */
    parseBoolean(dataValue, optionValue, defaultValue) {
        if (dataValue !== undefined) {
            return dataValue === 'true' || dataValue === true;
        }
        if (optionValue !== undefined) {
            return optionValue === 'true' || optionValue === true;
        }
        return defaultValue;
    }
    
    /**
     * Initialiser l'accordéon
     */
    init() {
        if (this.isInitialized) return;
        
        this.headers.forEach((header, index) => {
            const targetSelector = header.dataset.accordionTarget;
            const content = targetSelector 
                ? this.element.querySelector(targetSelector)
                : this.contents[index];
            
            if (!content) {
                console.warn(`Accordion: No content found for header ${index}`);
                return;
            }
            
            // Déterminer l'état initial
            const isOpenFromAttr = header.getAttribute('aria-expanded') === 'true';
            const isOpenFromStyle = content.style.display !== 'none';
            const isOpenFromClass = content.classList.contains('is-open');
            
            const item = {
                index,
                header,
                content,
                isOpen: isOpenFromAttr || isOpenFromStyle || isOpenFromClass
            };
            
            this.items.push(item);
            
            // Bind click event
            header.addEventListener('click', (e) => this.toggle(index, e));
            
            // Bind keyboard events
            header.addEventListener('keydown', (e) => this.handleKeydown(index, e));
            
            // S'assurer que l'état visuel est correct
            this.updateItemState(item, item.isOpen, false);
        });
        
        this.isInitialized = true;
        this.element.setAttribute('data-accordion-init', 'true');
        
        // console.log(`🪗 Accordion initialized: ${this.id}`, {
        //     items: this.items.length,
        //     options: this.options
        // });
    }
    
    /**
     * Toggle un item
     */
    toggle(index, event = null) {
        if (event) {
            event.preventDefault();
        }
        
        const item = this.items[index];
        if (!item) return;
        
        const willOpen = !item.isOpen;
        
        // Si on n'autorise qu'un seul ouvert, fermer les autres
        if (willOpen && !this.options.allowMultiple) {
            this.items.forEach((otherItem, otherIndex) => {
                if (otherIndex !== index && otherItem.isOpen) {
                    this.close(otherIndex);
                }
            });
        }
        
        if (willOpen) {
            this.open(index);
        } else {
            this.close(index);
        }
    }
    
    /**
     * Ouvrir un item
     */
    open(index) {
        const item = this.items[index];
        if (!item || item.isOpen) return;
        
        item.isOpen = true;
        this.updateItemState(item, true, this.options.duration > 0);
        
        // Dispatch event
        this.element.dispatchEvent(new CustomEvent('accordion:open', {
            detail: { index, item, accordion: this },
            bubbles: true
        }));
    }
    
    /**
     * Fermer un item
     */
    close(index) {
        const item = this.items[index];
        if (!item || !item.isOpen) return;
        
        item.isOpen = false;
        this.updateItemState(item, false, this.options.duration > 0);
        
        // Dispatch event
        this.element.dispatchEvent(new CustomEvent('accordion:close', {
            detail: { index, item, accordion: this },
            bubbles: true
        }));
    }
    
    /**
     * Ouvrir tous les items
     */
    openAll() {
        this.items.forEach((_, index) => this.open(index));
    }
    
    /**
     * Fermer tous les items
     */
    closeAll() {
        this.items.forEach((_, index) => this.close(index));
    }
    
    /**
     * Mettre à jour l'état visuel d'un item
     */
    updateItemState(item, isOpen, animate = true) {
        const { header, content } = item;
        
        // Mettre à jour aria
        header.setAttribute('aria-expanded', isOpen.toString());
        
        // Mettre à jour les classes
        header.classList.toggle('is-active', isOpen);
        content.classList.toggle('is-open', isOpen);
        
        // Gestion de l'icône selon le type
        this.updateIcon(header, isOpen);
        
        // Animation du contenu
        if (animate && this.options.duration > 0) {
            this.animateContent(content, isOpen);
        } else {
            // Pas d'animation
            content.style.display = isOpen ? 'block' : 'none';
            content.style.height = isOpen ? 'auto' : '0';
            content.style.overflow = isOpen ? 'visible' : 'hidden';
        }
    }
    
    /**
     * Mettre à jour l'icône selon le type et l'état
     */
    updateIcon(header, isOpen) {
        const iconWrapper = header.querySelector('.accordion-icon-wrapper');
        const icon = header.querySelector('.accordion-icon');
        
        if (!icon) return;
        
        if (this.options.iconType === 'plus') {
            // Pour le type "plus", on change le SVG path
            const path = icon.querySelector('path');
            if (path) {
                if (isOpen) {
                    // Moins
                    path.setAttribute('d', 'M20 12H4');
                } else {
                    // Plus
                    path.setAttribute('d', 'M12 4v16m8-8H4');
                }
            }
        } else if (this.options.iconRotate) {
            // Pour chevron et arrow, on fait une rotation
            icon.classList.toggle('rotate-180', isOpen);
        }
    }
    
    /**
     * Animer le contenu
     */
    animateContent(content, isOpen) {
        const { duration, easing } = this.options;
        
        // Annuler toute animation en cours
        if (content._accordionAnimation) {
            content._accordionAnimation.cancel();
        }
        
        if (isOpen) {
            // Ouvrir
            content.style.display = 'block';
            content.style.overflow = 'hidden';
            const height = content.scrollHeight;
            
            // Animation avec Web Animations API si disponible
            if (content.animate) {
                content._accordionAnimation = content.animate([
                    { height: '0px', opacity: 0 },
                    { height: `${height}px`, opacity: 1 }
                ], {
                    duration,
                    easing,
                    fill: 'forwards'
                });
                
                content._accordionAnimation.onfinish = () => {
                    content.style.height = 'auto';
                    content.style.overflow = 'visible';
                    content._accordionAnimation = null;
                };
            } else {
                // Fallback CSS transition
                content.style.height = '0px';
                content.offsetHeight; // Force reflow
                content.style.transition = `height ${duration}ms ${easing}, opacity ${duration}ms ${easing}`;
                content.style.height = `${height}px`;
                
                setTimeout(() => {
                    content.style.height = 'auto';
                    content.style.overflow = 'visible';
                    content.style.transition = '';
                }, duration);
            }
        } else {
            // Fermer
            const height = content.scrollHeight;
            content.style.overflow = 'hidden';
            
            if (content.animate) {
                content._accordionAnimation = content.animate([
                    { height: `${height}px`, opacity: 1 },
                    { height: '0px', opacity: 0 }
                ], {
                    duration,
                    easing,
                    fill: 'forwards'
                });
                
                content._accordionAnimation.onfinish = () => {
                    content.style.display = 'none';
                    content.style.height = '';
                    content._accordionAnimation = null;
                };
            } else {
                // Fallback CSS transition
                content.style.height = `${height}px`;
                content.offsetHeight; // Force reflow
                content.style.transition = `height ${duration}ms ${easing}, opacity ${duration}ms ${easing}`;
                content.style.height = '0px';
                
                setTimeout(() => {
                    content.style.display = 'none';
                    content.style.height = '';
                    content.style.transition = '';
                }, duration);
            }
        }
    }
    
    /**
     * Gérer les événements clavier (accessibilité)
     */
    handleKeydown(index, event) {
        const { key } = event;
        
        switch (key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.toggle(index);
                break;
                
            case 'ArrowDown':
                event.preventDefault();
                this.focusNext(index);
                break;
                
            case 'ArrowUp':
                event.preventDefault();
                this.focusPrevious(index);
                break;
                
            case 'Home':
                event.preventDefault();
                this.focusFirst();
                break;
                
            case 'End':
                event.preventDefault();
                this.focusLast();
                break;
        }
    }
    
    /**
     * Focus sur le header suivant
     */
    focusNext(currentIndex) {
        const nextIndex = (currentIndex + 1) % this.items.length;
        this.items[nextIndex]?.header.focus();
    }
    
    /**
     * Focus sur le header précédent
     */
    focusPrevious(currentIndex) {
        const prevIndex = (currentIndex - 1 + this.items.length) % this.items.length;
        this.items[prevIndex]?.header.focus();
    }
    
    /**
     * Focus sur le premier header
     */
    focusFirst() {
        this.items[0]?.header.focus();
    }
    
    /**
     * Focus sur le dernier header
     */
    focusLast() {
        this.items[this.items.length - 1]?.header.focus();
    }
    
    /**
     * Obtenir l'état actuel
     */
    getState() {
        return this.items.map(item => ({
            index: item.index,
            isOpen: item.isOpen
        }));
    }
    
    /**
     * Mettre à jour les options dynamiquement
     */
    updateOptions(newOptions) {
        this.options = { ...this.options, ...newOptions };
        console.log(`🪗 Accordion options updated: ${this.id}`, this.options);
    }
    
    /**
     * Détruire l'instance
     */
    destroy() {
        this.items.forEach(item => {
            item.header.removeEventListener('click', this.toggle);
            item.header.removeEventListener('keydown', this.handleKeydown);
            
            // Annuler les animations en cours
            if (item.content._accordionAnimation) {
                item.content._accordionAnimation.cancel();
            }
        });
        
        this.element.removeAttribute('data-accordion-init');
        accordionInstances.delete(this.id);
        
        console.log(`🪗 Accordion destroyed: ${this.id}`);
    }
}

/**
 * Initialiser les accordéons dans un élément
 * 
 * @param {HTMLElement} container - Conteneur à scanner
 * @param {Object} options - Options par défaut
 * @returns {Accordion[]} - Instances créées
 */
export function initAccordion(container = document, options = {}) {
    const accordions = container.querySelectorAll('.builder-accordion:not([data-accordion-init])');
    const instances = [];
    
    accordions.forEach(element => {
        // Vérifier si déjà initialisé
        if (element.hasAttribute('data-accordion-init')) {
            return;
        }
        
        const id = element.id || `accordion-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        element.id = id;
        
        const instance = new Accordion(element, options);
        accordionInstances.set(id, instance);
        instances.push(instance);
    });
    
    if (instances.length > 0) {
        // console.log(`🪗 Initialized ${instances.length} accordion(s)`);
    }
    
    return instances;
}

/**
 * Obtenir une instance d'accordéon par ID
 * 
 * @param {string} id - ID de l'accordéon
 * @returns {Accordion|undefined}
 */
export function getAccordion(id) {
    return accordionInstances.get(id);
}

/**
 * Obtenir toutes les instances d'accordéons
 * 
 * @returns {Map<string, Accordion>}
 */
export function getAllAccordions() {
    return accordionInstances;
}

/**
 * Rafraîchir tous les accordéons
 * 
 * @param {HTMLElement} container - Conteneur à scanner
 */
export function refreshAccordions(container = document) {
    // Détruire les instances orphelines
    accordionInstances.forEach((instance, id) => {
        if (!document.getElementById(id)) {
            instance.destroy();
        }
    });
    
    // Initialiser les nouveaux
    return initAccordion(container);
}

/**
 * Détruire un accordéon par ID
 * 
 * @param {string} id - ID de l'accordéon
 */
export function destroyAccordion(id) {
    const instance = accordionInstances.get(id);
    if (instance) {
        instance.destroy();
    }
}

/**
 * Détruire tous les accordéons
 */
export function destroyAllAccordions() {
    accordionInstances.forEach(instance => instance.destroy());
    accordionInstances.clear();
}

// Export de la classe pour usage avancé
export { Accordion };

// Export par défaut
export default {
    init: initAccordion,
    get: getAccordion,
    getAll: getAllAccordions,
    refresh: refreshAccordions,
    destroy: destroyAccordion,
    destroyAll: destroyAllAccordions,
    Accordion
};


/*
========================================
EXEMPLES D'UTILISATION
========================================

// Auto-init au chargement
document.addEventListener('DOMContentLoaded', () => {
    window.App?.accordion?.init();
});

// Obtenir une instance
const accordion = window.App.accordion.get('accordion-1');

// Ouvrir/fermer programmatiquement
accordion.open(0);
accordion.close(0);
accordion.toggle(1);
accordion.openAll();
accordion.closeAll();

// Mettre à jour les options
accordion.updateOptions({ allowMultiple: true });

// Écouter les événements
document.getElementById('accordion-1').addEventListener('accordion:open', (e) => {
    console.log('Opened item:', e.detail.index);
});

document.getElementById('accordion-1').addEventListener('accordion:close', (e) => {
    console.log('Closed item:', e.detail.index);
});

// Obtenir l'état
const state = accordion.getState();
// [{ index: 0, isOpen: true }, { index: 1, isOpen: false }, ...]


========================================
DATA-ATTRIBUTES SUPPORTÉS (via AccordionGenerator.php)
========================================

data-allow-multiple="true|false"  - Permettre plusieurs éléments ouverts
data-duration="300"              - Durée de l'animation en ms (0 = pas d'animation)
data-easing="ease-out"           - Fonction d'easing CSS
data-open-first="true|false"     - Ouvrir le premier élément par défaut
data-icon-rotate="true|false"    - Rotation de l'icône (chevron/arrow)
data-style="default|bordered|separated|minimal" - Style visuel
data-icon-type="chevron|plus|arrow" - Type d'icône
data-icon-position="left|right"  - Position de l'icône

*/


/*
// Obtenir une instance
const accordion = window.App.accordion.get('accordion-1');

// Ouvrir/fermer programmatiquement
accordion.open(0);
accordion.close(0);
accordion.toggle(1);
accordion.openAll();
accordion.closeAll();

// Écouter les événements
document.getElementById('accordion-1').addEventListener('accordion:open', (e) => {
    console.log('Opened item:', e.detail.index);
});

document.getElementById('accordion-1').addEventListener('accordion:close', (e) => {
    console.log('Closed item:', e.detail.index);
});
*/